import React, { useRef, useState } from 'react';
import { ArrowRight, FileKey } from 'lucide-react';
import { KeyStatusFilter, KeySummary, percentOf } from './keyRegisterView';
import { KEY_STATUS_PLURAL_LABELS } from './keyRegisterLabels';

interface KeySummaryCardsProps {
    summary: KeySummary;
    active: KeyStatusFilter | null;
    onToggle: (filter: KeyStatusFilter | null) => void;
    /** Number of Låsschema files; the card opens them and never filters. */
    lockScheduleCount: number;
    onOpenLockSchedules: () => void;
}

/** One choice in the status card. `null` is Alla nycklar. */
export interface StatusOption {
    filter: KeyStatusFilter | null;
    label: string;
    count: number;
    /** Colour class suffix of the swatch (`key-bar-seg--{tone}`, `overdue` from its own rule). */
    tone: string;
    /** Försenade sits under Ute: it is a part of it, not beside it. */
    sub?: boolean;
    /** Shown under the count: "50 %", "av 9 ute". */
    detail?: string;
}

/**
 * The options in order. Försenade shows while there are overdue keys, Kasserade while scrapped keys
 * are shown, and either one also while it is the active filter, so the selection never disappears.
 */
export const statusOptions = (summary: KeySummary, active: KeyStatusFilter | null): StatusOption[] => {
    const { total, counts, overdue } = summary;
    const pct = (count: number) => `${percentOf(count, total)} %`;
    const options: StatusOption[] = [
        { filter: null, label: 'Alla nycklar', count: total, tone: 'all' },
        { filter: 'inStock', label: KEY_STATUS_PLURAL_LABELS.inStock, count: counts.inStock, tone: 'inStock', detail: pct(counts.inStock) },
        { filter: 'out', label: KEY_STATUS_PLURAL_LABELS.handedOut, count: counts.handedOut, tone: 'handedOut', detail: pct(counts.handedOut) },
    ];
    if (overdue > 0 || active === 'overdue') {
        options.push({ filter: 'overdue', label: 'varav försenade', count: overdue, tone: 'overdue', sub: true, detail: `av ${counts.handedOut} ute` });
    }
    options.push(
        { filter: 'lost', label: KEY_STATUS_PLURAL_LABELS.lost, count: counts.lost, tone: 'lost', detail: pct(counts.lost) },
        { filter: 'blocked', label: KEY_STATUS_PLURAL_LABELS.blocked, count: counts.blocked, tone: 'blocked', detail: pct(counts.blocked) },
    );
    if (counts.scrapped > 0 || active === 'scrapped') {
        options.push({ filter: 'scrapped', label: KEY_STATUS_PLURAL_LABELS.scrapped, count: counts.scrapped, tone: 'scrapped', detail: pct(counts.scrapped) });
    }
    return options;
};

interface BarSegment {
    filter: KeyStatusFilter;
    tone: string;
    count: number;
}

/** The bar: Ute is split into its on-time part and its overdue part, so the segments add up to the total. */
const barSegments = ({ counts, overdue }: KeySummary): BarSegment[] => [
    { filter: 'inStock' as const, tone: 'inStock', count: counts.inStock },
    { filter: 'out' as const, tone: 'handedOut', count: counts.handedOut - overdue },
    { filter: 'overdue' as const, tone: 'overdue', count: overdue },
    { filter: 'lost' as const, tone: 'lost', count: counts.lost },
    { filter: 'blocked' as const, tone: 'blocked', count: counts.blocked },
    { filter: 'scrapped' as const, tone: 'scrapped', count: counts.scrapped },
].filter(segment => segment.count > 0);

/** Whether a bar segment belongs to the emphasised option (Ute covers its overdue part too). */
const segmentMatches = (segment: KeyStatusFilter, emphasised: KeyStatusFilter): boolean =>
    segment === emphasised || (emphasised === 'out' && segment === 'overdue');

const headline = (summary: KeySummary, active: KeyStatusFilter | null, options: StatusOption[]): { count: number; text: string } => {
    const option = options.find(o => o.filter === active);
    if (!active || !option) return { count: summary.total, text: summary.total === 1 ? 'nyckel' : 'nycklar' };
    return { count: option.count, text: `${option.label.replace(/^varav /, '').toLocaleLowerCase('sv')} av ${summary.total}` };
};

const fileCountLabel = (count: number): string => {
    if (count === 0) return 'Inga filer';
    return count === 1 ? '1 fil' : `${count} filer`;
};

/**
 * The card row under the header: one wide status card and the Låsschema card.
 * The status card is a radio group: each row filters the table (`?status=`), Alla nycklar clears it,
 * and choosing the active row again clears it too. The bar mirrors the choice (the rest fades) and
 * its segments choose like their rows; hovering a segment lights up its row, hovering a row its segment.
 * Låsschema opens the register's lock schedule files instead of filtering.
 */
const KeySummaryCards: React.FC<KeySummaryCardsProps> = ({ summary, active, onToggle, lockScheduleCount, onOpenLockSchedules }) => {
    const [hovered, setHovered] = useState<KeyStatusFilter | null>(null);
    const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const options = statusOptions(summary, active);
    const emphasised = hovered ?? active;
    const top = headline(summary, active, options);

    const choose = (filter: KeyStatusFilter | null) => onToggle(filter !== null && filter === active ? null : filter);

    // Radio group keys: arrows move and choose, Home/End jump.
    const onKeyDown = (event: React.KeyboardEvent, index: number) => {
        const last = options.length - 1;
        const next = {
            ArrowDown: index === last ? 0 : index + 1,
            ArrowRight: index === last ? 0 : index + 1,
            ArrowUp: index === 0 ? last : index - 1,
            ArrowLeft: index === 0 ? last : index - 1,
            Home: 0,
            End: last,
        }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        onToggle(options[next].filter);
        optionRefs.current[next]?.focus();
    };

    return (
        <div className="key-cards">
            <section className="key-card key-card--status" aria-label="Nycklar per status">
                <div className="key-status-top">
                    <span className="key-card-count">{top.count}</span>
                    <span className="key-status-top-text">{top.text}</span>
                </div>
                <div className="key-status-bar" aria-hidden="true" onMouseLeave={() => setHovered(null)}>
                    {barSegments(summary).map(segment => (
                        <span
                            key={segment.filter}
                            className={`key-status-seg key-status-seg--${segment.tone}${emphasised && !segmentMatches(segment.filter, emphasised) ? ' key-status-seg--dim' : ''}`}
                            style={{ flexGrow: segment.count }}
                            onMouseEnter={() => setHovered(segment.filter)}
                            onClick={() => choose(segment.filter)}
                        />
                    ))}
                </div>
                <div className="key-status-options" role="radiogroup" aria-label="Filtrera på status" onMouseLeave={() => setHovered(null)}>
                    {options.map((option, index) => {
                        const checked = option.filter === active;
                        return (
                            <button
                                key={option.filter ?? 'all'}
                                ref={el => { optionRefs.current[index] = el; }}
                                type="button"
                                role="radio"
                                aria-checked={checked}
                                tabIndex={checked || (active === null && option.filter === null) ? 0 : -1}
                                className={[
                                    'key-status-option',
                                    `key-status-option--${option.tone}`,
                                    option.sub ? 'key-status-option--sub' : '',
                                    checked ? 'key-status-option--checked' : '',
                                    hovered !== null && option.filter === hovered ? 'key-status-option--hover' : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => choose(option.filter)}
                                onKeyDown={event => onKeyDown(event, index)}
                                onMouseEnter={() => setHovered(option.filter)}
                            >
                                <span className="key-status-option-label">
                                    <span className={`key-legend-swatch key-status-swatch--${option.tone}`} aria-hidden="true" />
                                    {option.label}
                                </span>
                                <span className="key-status-option-count">{option.count}</span>
                                {option.detail && <span className="key-status-option-detail">{option.detail}</span>}
                            </button>
                        );
                    })}
                </div>
            </section>
            <button type="button" className="key-card key-card--schedule" onClick={onOpenLockSchedules}>
                <span className="key-card-head">
                    <span className="key-card-label">Låsschema</span>
                    <span className="key-card-icon" aria-hidden="true"><FileKey size={22} /></span>
                </span>
                <span className="key-card-schedule-count">{fileCountLabel(lockScheduleCount)}</span>
                <span className="key-card-foot">
                    <span>{lockScheduleCount === 0 ? 'Lägg till fil' : 'Visa filer'}</span>
                    <ArrowRight size={16} aria-hidden="true" />
                </span>
            </button>
        </div>
    );
};

export default KeySummaryCards;
